import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../api';
import { entityKind, entityPath } from '../entityIds';

export interface EntityInfoCardProps {
  entityId: string;
  entities: { entityId: string; name: string }[];
  onClose: () => void;
  /** Bare book UUID of the active storyboard, used to scope the World Builder link. */
  bookId?: string | null;
  /** In a read-only embed the World Builder opens in a new window (#1034). */
  readonly?: boolean;
}

interface EntityDetail {
  entityId: string;
  name: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

const TYPE_COLOR: Record<string, string> = {
  character: '#196773',
  location: '#5CA383',
  event: '#c97a1e',
};

const TYPE_LABEL: Record<string, string> = {
  character: 'Character',
  location: 'Location',
  event: 'Event',
};

function parseEntityId(entityId: string): { entityType: string; apiPath: string } {
  return { entityType: entityKind(entityId), apiPath: entityPath(entityId) };
}

const EntityInfoCard: React.FC<EntityInfoCardProps> = ({ entityId, entities, onClose, bookId, readonly = false }) => {
  const navigate = useNavigate();

  // Open the World Builder filtered to the active storyboard's book, with this
  // entity selected (RelationshipPage reads `scope`/`id` for the filter and
  // `selected` to auto-select the node and open its side panel).
  const openInWorldBuilder = () => {
    const params = new URLSearchParams();
    if (bookId) {
      params.set('scope', 'book');
      params.set('id', bookId);
    }
    params.set('selected', entityId);
    // From a read-only embed it opens in a new window, as Edit does: the desktop sends that
    // to the system browser, where the user's own sign-in applies. Navigating here would
    // leave the read-only embeds, which an embed tab shows as a notice (#1034).
    if (readonly) {
      window.open(`${window.location.origin}/world?${params.toString()}`, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(`/world?${params.toString()}`);
  };
  const [entity, setEntity] = useState<EntityDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const { entityType, apiPath } = parseEntityId(entityId);
  const color = TYPE_COLOR[entityType] ?? '#196773';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEntity(null);
    apiGet(apiPath)
      .then(r => r.body.json() as unknown as Promise<EntityDetail>)
      .then(d => { if (!cancelled) setEntity(d); })
      .catch(() => { if (!cancelled) setEntity(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiPath]);

  const m = entity?.metadata ?? {};
  const str = (key: string) => (m[key] as string | undefined) || '';

  const characterSections = [
    {
      title: 'Appearance',
      fields: [
        { label: 'Age', value: str('age') },
        { label: 'Build', value: str('build') },
        { label: 'Distinguishing features', value: str('distinguishingFeatures') },
      ],
    },
    {
      title: 'Personality',
      fields: [
        { label: 'Core traits', value: str('coreTraits') },
        { label: 'Fatal flaw', value: str('fatalFlaw') },
        { label: 'Motivation', value: str('motivation') },
      ],
    },
    {
      title: 'Backstory',
      fields: [
        { label: 'Origin', value: str('origin') },
        { label: 'Key life events', value: str('keyLifeEvents') },
      ],
    },
    {
      title: 'Story Role',
      fields: [
        { label: 'Role type', value: str('roleType') },
        { label: 'Archetype', value: str('archetype') },
        { label: 'Arc summary', value: str('arcSummary') },
        { label: 'Voice / dialect notes', value: str('voiceDialectNotes') },
      ],
    },
  ];

  const locationSections = [
    {
      title: 'Geography',
      fields: [
        { label: 'Climate', value: str('climate') },
        { label: 'Terrain', value: str('terrain') },
        { label: 'Notable landmarks', value: str('notableLandmarks') },
      ],
    },
    {
      title: 'Culture',
      fields: [
        { label: 'Government / authority', value: str('government') },
        { label: 'Customs & beliefs', value: str('customsBeliefs') },
        { label: 'Economy', value: str('economy') },
      ],
    },
    {
      title: 'History',
      fields: [
        { label: 'Founding / origin', value: str('foundingOrigin') },
        { label: 'Current state', value: str('currentState') },
      ],
    },
  ];

  const participantCharNames = ((m.participantCharacterIds as string[] | undefined) ?? [])
    .map(id => entities.find(e => e.entityId === id)?.name ?? id)
    .join(', ');
  const participantLocId = (m.participantLocationId as string | undefined) ?? '';
  const participantLocName = participantLocId
    ? (entities.find(e => e.entityId === participantLocId)?.name ?? participantLocId)
    : '';

  const eventSections = [
    {
      title: 'Timeline',
      fields: [
        { label: 'When it occurs', value: str('whenItOccurs') },
        { label: 'Duration', value: str('duration') },
      ],
    },
    {
      title: 'Participants',
      fields: [
        { label: 'Characters involved', value: participantCharNames },
        { label: 'Location', value: participantLocName },
      ],
    },
    {
      title: 'Story Impact',
      fields: [
        { label: 'Cause', value: str('cause') },
        { label: 'Consequence', value: str('consequence') },
        { label: 'POV character', value: str('povCharacter') },
      ],
    },
  ];

  const sections = entityType === 'character' ? characterSections
    : entityType === 'location' ? locationSections
    : eventSections;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={onClose} />
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: 320,
        height: '100vh',
        background: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.14)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 16px 12px',
          borderBottom: `3px solid ${color}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          flexShrink: 0,
        }}>
          <div style={{ flex: 1 }}>
            {loading
              ? <div style={{ height: 22, background: '#f0f1f3', borderRadius: 4, width: '60%', marginBottom: 8 }} />
              : <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 6 }}>
                  {entity?.name ?? '—'}
                </div>
            }
            <span style={{
              background: color, color: '#fff', borderRadius: 3,
              padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            }}>
              {TYPE_LABEL[entityType]}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#999', fontSize: 22, lineHeight: 1, padding: '0 2px', marginTop: -2,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {loading && (
            <div style={{ color: '#aaa', fontSize: 13 }}>Loading…</div>
          )}
          {!loading && !entity && (
            <div style={{ color: '#c0392b', fontSize: 13 }}>Could not load entity.</div>
          )}
          {!loading && entity && (<>
            {entity.description && (
              <div style={{ marginBottom: 16 }}>
                <div style={sectionTitleStyle(color)}>Description</div>
                <div style={bodyTextStyle}>{entity.description}</div>
              </div>
            )}
            {sections.map(section => {
              const populated = section.fields.filter(f => f.value);
              if (populated.length === 0) return null;
              return (
                <div key={section.title} style={{ marginBottom: 16 }}>
                  <div style={sectionTitleStyle(color)}>{section.title}</div>
                  {populated.map(f => (
                    <div key={f.label} style={{ marginBottom: 8 }}>
                      <div style={fieldLabelStyle}>{f.label}</div>
                      <div style={bodyTextStyle}>{f.value}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </>)}
        </div>

        {/* Footer */}
        <div style={{ padding: 16, borderTop: '1px solid #eee', flexShrink: 0 }}>
          <button
            onClick={openInWorldBuilder}
            style={{
              width: '100%',
              background: color,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '9px 0',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: 0.2,
            }}
          >
            Open in World Builder →
          </button>
        </div>
      </div>
    </>
  );
};

const sectionTitleStyle = (color: string): React.CSSProperties => ({
  fontSize: 10,
  fontWeight: 700,
  color,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  marginBottom: 6,
});

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#999',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  marginBottom: 2,
};

const bodyTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#333',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

export default EntityInfoCard;
